import type { IEmbedConfiguration } from "powerbi-client";
import { models } from "powerbi-client";
import { useEffect } from "react";

import { useAppSelector } from "@/reduxHooks";
import { AnalyticsEvents } from "@/utils/analytics-events";
import { analyticsService } from "@/utils/analytics-service";

import { useGetDashboardsQuery, useGetEmbedTokenQuery } from "../api";
import { selectSessionId } from "../redux/selectors";


export const usePowerBiEmbedConfig = (analysisId: string | undefined) => {
  const {
    data: dashboardsData,
    isLoading: isDashboardsLoading,
    error: dashboardsError,
  } = useGetDashboardsQuery({ analysisId: analysisId! }, { skip: !analysisId });

  const sessionId = useAppSelector(selectSessionId);

  useEffect(() => {
    analyticsService.trackEvent(
      AnalyticsEvents.VIEW_DASHBOARD.START_LOADING,
      {
        analysisId: analysisId,
        sessionId: sessionId,
      }
    );
  }, []);

  // Assuming the first dashboard in the list is the one to embed
  const dashboardId = dashboardsData?.response?.data?.[0]?.id;
  const vizReportId =
    dashboardsData?.response?.data?.[0]?.dashboardDetails?.[0]?.vizReportId;

  const {
    data: embedTokenData,
    isLoading: isTokenLoading,
    error: tokenError,
  } = useGetEmbedTokenQuery(
    { analysisId: analysisId!, dashboardId: dashboardId! },
    { skip: !analysisId || !dashboardId } // Skip if we don't have analysisId or dashboardId
  );

  let embedConfig: IEmbedConfiguration | null = null;
  if (embedTokenData?.response?.data?.embedInfo && vizReportId) {
    const { embedToken, embedUrl } = embedTokenData.response.data.embedInfo;
    embedConfig = {
      type: "report",
      id: vizReportId,  
      embedUrl: embedUrl,
      accessToken: embedToken,
      tokenType: models.TokenType.Embed,
      // settings: DEFAULT_EMBED_SETTINGS, // Apply settings here or in PowerBIEmbed component
    };
  }

  const isLoading = isDashboardsLoading || isTokenLoading;
  const error = dashboardsError || tokenError;

  return { embedConfig, isLoading, error };
}; 
